import React, { useState, useEffect, useCallback } from 'react';
import programService from '../api/programService';
import moduleService from '../api/moduleService';
import authService from '../api/authService';
import { getFrameworkLabel, getFrameworkColor } from '../constants/moduleCategories';
import './ManagementPages.css';
import './Modal.css';

const ProgramManagement = () => {
  const [programs, setPrograms] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Modal and Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const initialFormState = {
    id: null,
    program_name: '',
    description: '',
    framework_weeks: 9,
    batch_code_start: '',
    moduleIds: [],
  };
  const [formData, setFormData] = useState(initialFormState);

  // Permissions
  const currentUser = authService.getCurrentUser();
  const isScheduler = currentUser && currentUser.role === 'scheduler';

  // --- Data Fetching ---
  const fetchPrograms = useCallback(() => {
    setLoading(true);
    programService.getAllPrograms({ search: searchTerm })
      .then(response => {
        setPrograms(response.data);
      })
      .catch(error => console.error("Error fetching programs:", error))
      .finally(() => setLoading(false));
  }, [searchTerm]);

  const fetchModules = useCallback(() => {
    moduleService.getAllModules()
      .then(response => {
        setModules(response.data);
      })
      .catch(error => console.error("Error fetching modules:", error));
  }, []);

  useEffect(() => {
    fetchPrograms();
    fetchModules();
  }, [fetchPrograms, fetchModules]);

  // --- Modal and Form Handlers ---
  const handleOpenAddModal = () => {
    setIsEditing(false);
    setFormData(initialFormState);
    setFieldErrors({});
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (program) => {
    setIsEditing(true);
    setFormData({
      ...program,
      framework_weeks: program.framework_weeks ?? 9,
      batch_code_start: program.batch_code_start ?? '',
      moduleIds: program.modules ? program.modules.map(m => m.id) : [],
    });
    setFieldErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFieldErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleModuleToggle = (moduleId) => {
    setFormData(prev => {
      const currentIds = prev.moduleIds || [];
      const newIds = currentIds.includes(moduleId)
        ? currentIds.filter(id => id !== moduleId)
        : [...currentIds, moduleId];
      return { ...prev, moduleIds: newIds };
    });
  };

  // Parse error response and extract field-level errors
  const parseErrors = (error) => {
    const errors = {};
    const response = error.response?.data;

    if (response?.errors && Array.isArray(response.errors)) {
      response.errors.forEach(err => {
        const field = err.path || err.param || err.field;
        if (field) {
          errors[field] = err.msg || err.message;
        }
      });
    } else if (response?.message) {
      const msg = response.message.toLowerCase();
      if (msg.includes('program_name') || msg.includes('name')) {
        errors.program_name = response.message;
      } else if (msg.includes('framework')) {
        errors.framework_weeks = response.message;
      } else if (msg.includes('description')) {
        errors.description = response.message;
      } else if (msg.includes('batch code start')) {
        errors.batch_code_start = response.message;
      } else {
        errors._general = response.message;
      }
    } else {
      errors._general = 'Could not save program. Please check your input.';
    }
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});

    const payload = {
      ...formData,
      framework_weeks: Number(formData.framework_weeks || 9),
      batch_code_start:
        formData.batch_code_start === '' || formData.batch_code_start === null
          ? null
          : Number(formData.batch_code_start),
    };

    const action = isEditing
      ? programService.updateProgram(payload.id, payload)
      : programService.createProgram(payload);

    action.then(() => {
      fetchPrograms();
      handleCloseModal();
    }).catch(err => {
      const errors = parseErrors(err);
      setFieldErrors(errors);
    });
  };

  const handleDeleteClick = (programId) => {
    if (window.confirm('Are you sure you want to delete this program?')) {
      programService.deleteProgram(programId).then(() => {
        fetchPrograms();
      });
    }
  };

  // Calculate stats for compact header
  const totalPrograms = programs.length;

  return (
    <div className="management-page">
      {/* Compact Header with inline stats and Add button */}
      <div className="page-header-compact">
        <div className="header-left">
          <h1 className="page-title">
            <span className="page-title-icon">🎯</span> Program Management
          </h1>
          <div className="inline-stats">
            <span className="stat-item">📊 {totalPrograms} Programs</span>
          </div>
        </div>
        {isScheduler && (
          <button className="add-btn-primary" onClick={handleOpenAddModal}>
            + Add Program
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="filter-bar fade-in">
        <div className="search-field">
          <input
            type="text"
            placeholder="🔍 Search programs..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner fade-in">
          <div className="spinner"></div>
          <span>Loading program data...</span>
        </div>
      ) : (
        <table className="management-table fade-in">
          <thead>
            <tr>
              <th>Program Name</th>
              <th>Framework</th>
              <th>Modules</th>
              <th>Batch Start #</th>
              <th>Description</th>
              {isScheduler && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {programs.map(program => (
              <tr key={program.id}>
                <td>
                  <strong>{program.program_name}</strong>
                </td>
                <td>
                  <span
                    className="framework-badge"
                    style={{ backgroundColor: getFrameworkColor(program.framework_weeks) }}
                  >
                    {getFrameworkLabel(program.framework_weeks)}
                  </span>
                </td>
                <td>
                  {program.modules && program.modules.length > 0 ? (
                    <div className="module-tags">
                      {program.modules.map(m => (
                        <span key={m.id} className="module-tag">{m.name}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="no-modules">No modules</span>
                  )}
                </td>
                <td>{program.batch_code_start || '—'}</td>
                <td className="description-cell">
                  {program.description ? program.description.substring(0, 80) + '...' : 'N/A'}
                </td>
                {isScheduler && (
                  <td className="actions-cell">
                    <button
                      className="action-button edit"
                      onClick={() => handleOpenEditModal(program)}
                      title="Edit program"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="action-button delete"
                      onClick={() => handleDeleteClick(program.id)}
                      title="Delete program"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal Dialog */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isEditing ? '✏️ Edit Program' : '➕ Add New Program'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {fieldErrors._general && (
                <div className="form-error-banner">
                  ⚠️ {fieldErrors._general}
                </div>
              )}
              <div className="form-grid">
                <div className={`form-field full-width ${fieldErrors.program_name ? 'has-error' : ''}`}>
                  <label>🎯 Program Name</label>
                  <input
                    type="text"
                    name="program_name"
                    value={formData.program_name}
                    onChange={handleInputChange}
                    placeholder="e.g., Digital Marketing Bootcamp"
                    required
                    className={fieldErrors.program_name ? 'input-error' : ''}
                  />
                  {fieldErrors.program_name && <span className="field-error">{fieldErrors.program_name}</span>}
                </div>

                <div className={`form-field full-width ${fieldErrors.description ? 'has-error' : ''}`}>
                  <label>📝 Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Program description and overview"
                    rows="4"
                    className={fieldErrors.description ? 'input-error' : ''}
                  ></textarea>
                  {fieldErrors.description && <span className="field-error">{fieldErrors.description}</span>}
                </div>

                <div className={`form-field ${fieldErrors.framework_weeks ? 'has-error' : ''}`}>
                  <label>📋 Framework Length (Weeks)</label>
                  <input
                    type="number"
                    name="framework_weeks"
                    min="1"
                    max="52"
                    value={formData.framework_weeks}
                    onChange={handleInputChange}
                    placeholder="e.g., 9 for 2-month, 14 for 3-month"
                    className={fieldErrors.framework_weeks ? 'input-error' : ''}
                  />
                  {fieldErrors.framework_weeks
                    ? <span className="field-error">{fieldErrors.framework_weeks}</span>
                    : <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                        Number of weeks in this program's session framework. This
                        caps the schedule (e.g. 9 ≈ 2 months, 14 ≈ 3 months).
                      </small>}
                </div>

                <div className={`form-field full-width ${fieldErrors.batch_code_start ? 'has-error' : ''}`}>
                  <label>🔢 Batch Code Start Number</label>
                  <input
                    type="number"
                    name="batch_code_start"
                    min="1"
                    value={formData.batch_code_start}
                    onChange={handleInputChange}
                    placeholder="e.g., 327 — optional"
                    className={fieldErrors.batch_code_start ? 'input-error' : ''}
                  />
                  {fieldErrors.batch_code_start
                    ? <span className="field-error">{fieldErrors.batch_code_start}</span>
                    : <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                        The first batch created for this program will use this number
                        (e.g. 327 → BSM-327). Leave blank to start numbering from 1.
                      </small>}
                </div>

                <div className="form-field full-width">
                  <label>📚 Modules ({formData.moduleIds?.length || 0} selected)</label>
                  <div className="module-checkbox-list">
                    {modules.length > 0 ? (
                      modules.map(module => (
                        <label key={module.id} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.moduleIds?.includes(module.id) || false}
                            onChange={() => handleModuleToggle(module.id)}
                          />
                          <span className="checkbox-label">
                            {module.name}
                            {module.module_code && <span className="module-code-small">({module.module_code})</span>}
                          </span>
                        </label>
                      ))
                    ) : (
                      <span className="no-modules-msg">No modules available. Create modules first.</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={handleCloseModal}>
                  ❌ Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {isEditing ? '💾 Save Changes' : '➕ Add Program'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramManagement;